import { Link, useRouter } from "@tanstack/react-router";
// import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input, InputPassword } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useSignIn, useSession } from "@wacht/tanstack-router";

const Signin = () => {
  // Add maintenance mode state
  const [maintenanceMode] = useState(false); // Set to true to show maintenance message

  // Add countdown state
  const [timeRemaining, setTimeRemaining] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { signinAttempt, signIn } = useSignIn();
  const { refetch } = useSession();

  // Countdown timer effect
  useEffect(() => {
    if (!maintenanceMode) return;

    // Set target time to 7:00 PM today
    const calculateTimeRemaining = () => {
      const now = new Date();
      const targetTime = new Date(now);
      targetTime.setHours(19, 0, 0, 0); // 7:00 PM

      // If it's already past 7 PM, set target to tomorrow
      if (now > targetTime) {
        targetTime.setDate(targetTime.getDate() + 1);
      }

      const diffMs = targetTime.getTime() - now.getTime();
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const diffSeconds = Math.floor((diffMs % (1000 * 60)) / 1000);

      return {
        hours: diffHours,
        minutes: diffMinutes,
        seconds: diffSeconds,
      };
    };

    // Initial calculation
    setTimeRemaining(calculateTimeRemaining());

    // Update countdown every second
    const timer = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining());
    }, 1000);

    return () => clearInterval(timer);
  }, [maintenanceMode]);

  const router = useRouter();

  const handleSignIn = async () => {
    // Clear previous errors
    setError("");

    // Basic validation
    if (!email.trim()) {
      setError("Email is required");
      toast.error("Please enter your email address");
      return;
    }

    if (!password.trim()) {
      setError("Password is required");
      toast.error("Please enter your password");
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError("Please enter a valid email address");
      toast.error("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    
    try {
      const signin = signIn.createStrategy("email");
      const result = await signin({ email, password });

      if (result.errors) {
        const errorMessage = result.errors[0]?.message || "Sign in failed. Please try again.";
        setError(errorMessage);
        toast.error(errorMessage);
        setIsLoading(false);
      } else {
        // Success will be handled by the useEffect watching signinAttempt
        toast.loading("Signing you in...", { id: "signin-loading" });
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (signinAttempt?.completed) {
      toast.dismiss("signin-loading");
      toast.success("Successfully signed in! Redirecting...", { duration: 2000 });
      
      refetch()
        .then(() => {
          setIsLoading(false);
          router.navigate({ to: "/dashboard" });
        })
        .catch((err: unknown) => {
          setIsLoading(false);
          const errorMessage = err instanceof Error ? err.message : "Failed to refresh session. Please try again.";
          toast.error(errorMessage);
        });
    }
  }, [signinAttempt, router, refetch]);

  const handleForgotPassword = () => {
    // Navigate to reset password page without requiring email first
    router.navigate({ to: "/reset-password" });
  };

  // Maintenance Message Component
  const MaintenanceMessage = () => (
    <div className="flex flex-col items-center justify-center h-full space-y-6 text-center">
      <div className="w-24 h-24 rounded-full bg-amber-100 flex items-center justify-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-amber-500"
        >
          <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
        </svg>
      </div>
      <h2 className="text-3xl font-bold">We're under maintenance</h2>
      <p className="text-muted-foreground max-w-md">
        We're making some improvements to our system. We'll be back online by
        7:00 PM.
      </p>

      {/* Countdown Timer */}
      <div className="mt-6">
        <p className="text-sm text-muted-foreground mb-3">
          Time until we're live:
        </p>
        <div className="flex gap-4 justify-center">
          <div className="flex flex-col items-center">
            <div className="bg-primary/10 rounded-lg w-16 h-16 flex items-center justify-center">
              <span className="text-2xl font-bold text-primary">
                {timeRemaining.hours.toString().padStart(2, "0")}
              </span>
            </div>
            <span className="text-xs mt-1 text-muted-foreground">Hours</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="bg-primary/10 rounded-lg w-16 h-16 flex items-center justify-center">
              <span className="text-2xl font-bold text-primary">
                {timeRemaining.minutes.toString().padStart(2, "0")}
              </span>
            </div>
            <span className="text-xs mt-1 text-muted-foreground">Minutes</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="bg-primary/10 rounded-lg w-16 h-16 flex items-center justify-center">
              <span className="text-2xl font-bold text-primary">
                {timeRemaining.seconds.toString().padStart(2, "0")}
              </span>
            </div>
            <span className="text-xs mt-1 text-muted-foreground">Seconds</span>
          </div>
        </div>
      </div>

      <p className="text-sm text-muted-foreground mt-4">
        Thank you for your patience.
      </p>
    </div>
  );

  return (
    <section className="bg-background p-2 ">
      <div className="relative grid grid-cols-1 lg:grid-cols-2 w-full">
        {/* Left Purple Section */}
        <div className="relative flex-col bg-[#0a0a0a] text-white hidden lg:flex justify-between items-center rounded-2xl overflow-hidden h-[calc(100dvh-16px)]">
          <div className="absolute inset-0 w-full h-full p-10">
            <img
              src="/id_full_logo_white.png"
              alt="InboxDoctor"
              className="h-[50px] w-auto"
            />

            <div className="flex flex-col gap-2 relative top-[8%] lg:top-[10%] left-[8%] max-w-[500px]">
              <h1 className="text-3xl/[30px] xl:text-[40px]/[40px] font-bold ">
                Elevate Your Inboxing Game to New Heights
              </h1>
              <p className="text-lg text-gray-400">
                Boost email deliverability, warm-up automation, and domain
                monitoring to keep emails out of spam!
              </p>
            </div>
          </div>

          <img
            src="/auth-image.jpg"
            alt="InboxDoctor Interface"
            className="w-full h-full object-cover object-left-top"
          />
        </div>

        {/* Right Sign-in Section */}
        <div className="p-4 lg:p-8 h-[calc(100dvh-16px)] overflow-y-auto ">
          <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:max-w-[400px] h-full">
            {/* Show either maintenance message or login form based on maintenance mode */}
            {maintenanceMode ? (
              <MaintenanceMessage />
            ) : (
              <>
                <div className="flex flex-col space-y-2 text-center lg:text-left">
                  <div className="lg:hidden flex justify-center items-center mb-6">
                    <img
                      src="/id_full_logo_black.png"
                      alt="InboxDoctor"
                      className="h-[50px] w-auto dark:hidden"
                    />
                    <img
                      src="/id_full_logo_white.png"
                      alt="InboxDoctor"
                      className="h-[50px] w-auto hidden dark:block"
                    />
                  </div>

                  <h1 className="text-4xl font-semibold tracking-tight pb-14 text-center hidden lg:block">
                    Login
                  </h1>

                  <h3 className="text-2xl font-semibold tracking-tight">
                    Login to your account
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    New to Inbox Doctor?{" "}
                    <Link
                      to="/sign-up"
                      className="text-primary hover:underline"
                    >
                      Sign up
                    </Link>
                  </p>
                </div>

                {/* <form onSubmit={handleSignIn}> */}
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="email" className="text-sm font-medium">
                      Email
                    </Label>
                    <Input
                      id="email"
                      placeholder="name@example.com"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (error) setError(""); // Clear error when user starts typing
                      }}
                      autoCapitalize="none"
                      autoComplete="email"
                      autoCorrect="off"
                      disabled={isLoading}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !isLoading) {
                          handleSignIn();
                        }
                      }}
                    />
                  </div>
                  <div>
                    <Label htmlFor="password" className="text-sm font-medium">
                      Password
                    </Label>
                    <InputPassword
                      id="password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (error) setError(""); // Clear error when user starts typing
                      }}
                      disabled={isLoading}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !isLoading) {
                          handleSignIn();
                        }
                      }}
                    />
                  </div>

                  {/* Forgot Password Link */}

                  {error && <p className="text-red-500 text-sm">{error}</p>}
                  <Button
                    onClick={handleSignIn}
                    disabled={isLoading}
                    className="w-full bg-primary hover:bg-primary/80"
                  >
                    {isLoading ? "Signing in..." : "Sign In"}
                  </Button>
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className="text-sm text-primary hover:underline font-medium"
                    disabled={isLoading}
                  >
                    Forgot password?
                  </button>
                </div>

                <p className="text-center lg:text-left text-sm text-muted-foreground">
                  By clicking continue, you agree to our{" "}
                  <a
                    href="https://inboxdoctor.ai/legal/terms-and-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline-offset-2 hover:underline font-medium text-primary"
                  >
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://inboxdoctor.ai/legal/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline-offset-2 hover:underline font-medium text-primary"
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Signin;
